import React, { useState, useMemo } from 'react';
import { 
  Shield, 
  FileText, 
  CheckCircle2, 
  Download,
  Upload,
  Settings,
  Eye,
  Plus,
  Search,
  Award,
  BookOpen,
  Gavel,
  Target,
  TrendingUp,
  Calendar,
  Filter,
  } from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import type { ComplianceStandard } from '../../../types/security'
import { cn } from '../../../utils/cn'
import { formatAbsoluteTime, formatRelativeTime } from '../../../utils/format'

// Mock compliance data
const mockComplianceStandards: ComplianceStandard[] = [
  {
    id: 'soc2',
    name: 'SOC2',
    status: 'compliant',
    score: 92.1,
    requirements: [
      {
        id: 'soc2-cc1.1',
        name: 'Control Environment',
        status: 'met',
        evidence: [
          {
            id: 'ev-1',
            type: 'document',
            source: 'Security Policy v2.1',
            timestamp: new Date('2024-01-15'),
            hash: 'sha256:abcd1234...',
            metadata: { version: '2.1', approved: true },
          },
        ],
        lastChecked: new Date('2024-02-15'),
        automaticCheck: true,
      },
      {
        id: 'soc2-cc2.1',
        name: 'Communication and Information',
        status: 'met',
        evidence: [],
        lastChecked: new Date('2024-02-10'),
        automaticCheck: false,
      },
      {
        id: 'soc2-cc3.1',
        name: 'Risk Assessment',
        status: 'partial',
        evidence: [],
        lastChecked: new Date('2024-02-01'),
        automaticCheck: true,
      },
    ],
    lastAssessment: new Date('2024-01-01'),
    nextAssessment: new Date('2024-07-01'),
  },
  {
    id: 'pci_dss',
    name: 'PCI_DSS',
    status: 'partial',
    score: 78.5,
    requirements: [
      {
        id: 'pci-1.1',
        name: 'Firewall Configuration',
        status: 'met',
        evidence: [],
        lastChecked: new Date('2024-02-15'),
        automaticCheck: true,
      },
      {
        id: 'pci-2.1',
        name: 'Default Passwords',
        status: 'not_met',
        evidence: [],
        lastChecked: new Date('2024-02-10'),
        automaticCheck: true,
      },
    ],
    lastAssessment: new Date('2024-01-15'),
    nextAssessment: new Date('2024-04-15'),
  },
  {
    id: 'iso_27001',
    name: 'ISO_27001',
    status: 'non_compliant',
    score: 65.3,
    requirements: [
      {
        id: 'iso-5.1',
        name: 'Information Security Policies',
        status: 'met',
        evidence: [],
        lastChecked: new Date('2024-02-01'),
        automaticCheck: false,
      },
      {
        id: 'iso-6.1',
        name: 'Organization of Information Security',
        status: 'not_met',
        evidence: [],
        lastChecked: new Date('2024-01-30'),
        automaticCheck: false,
      },
    ],
    lastAssessment: new Date('2024-01-01'),
    nextAssessment: new Date('2024-06-01'),
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    status: 'compliant',
    score: 89.7,
    requirements: [
      {
        id: 'gdpr-25',
        name: 'Data Protection by Design',
        status: 'met',
        evidence: [],
        lastChecked: new Date('2024-02-10'),
        automaticCheck: true,
      },
      {
        id: 'gdpr-32',
        name: 'Security of Processing',
        status: 'met',
        evidence: [],
        lastChecked: new Date('2024-02-05'),
        automaticCheck: true,
      },
    ],
    lastAssessment: new Date('2024-01-15'),
    nextAssessment: new Date('2024-07-15'),
  },
];

export const ComplianceManagement: React.FC = () => {
  const [selectedStandard, setSelectedStandard] = useState<string | null>(null);
  const [selectedRequirement, setSelectedRequirement] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [_showCreateModal, setShowCreateModal] = useState(false);

  const filteredStandards = useMemo(() => {
    let filtered = mockComplianceStandards;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(standard =>
        standard.name.toLowerCase().includes(search) ||
        standard.requirements.some((req: any) =>
          req.name.toLowerCase().includes(search) ||
          req.id.toLowerCase().includes(search),
        ),
      );
    }

    if (statusFilter.length > 0) {
      filtered = filtered.filter(standard => statusFilter.includes(standard.status));
    }

    return filtered;
  }, [searchTerm, statusFilter]);

  const complianceOverview = useMemo(() => {
    const total = mockComplianceStandards.length;
    const compliant = mockComplianceStandards.filter(s => s.status === 'compliant').length;
    const nonCompliant = mockComplianceStandards.filter(s => s.status === 'non_compliant').length;
    const partial = mockComplianceStandards.filter(s => s.status === 'partial').length;
    const averageScore = mockComplianceStandards.reduce((sum, s) => sum + s.score, 0) / total;

    const allRequirements = mockComplianceStandards.flatMap(s => s.requirements);
    const metRequirements = allRequirements.filter(r => r.status === 'met').length;
    const totalRequirements = allRequirements.length;

    return {
      total,
      compliant,
      nonCompliant,
      partial,
      averageScore,
      requirementsMet: metRequirements,
      totalRequirements,
    };
  }, []);

  const upcomingAssessments = useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    return mockComplianceStandards.filter(standard => 
      standard.nextAssessment <= thirtyDaysFromNow,
    ).sort((a, b) => a.nextAssessment.getTime() - b.nextAssessment.getTime());
  }, []);

  const handleRunAssessment = (standardId: string) => {
    console.log(`Running assessment for: ${standardId}`);
  };

  const handleViewEvidence = (evidenceId: string) => {
    console.log(`Viewing evidence: ${evidenceId}`);
  };

  const handleExportReport = (standardId: string) => {
    console.log(`Exporting compliance report for: ${standardId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-600 rounded-lg">
            <Gavel className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-console-text">Compliance Management</h1>
            <p className="text-console-muted">Regulatory compliance tracking and assessment</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm">
            <Upload className="w-4 h-4 mr-2" />
            Import Framework
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Standard
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Overall Score</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceOverview.averageScore.toFixed(1)}%
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3 text-status-online" />
                <span className="text-xs text-status-online">+3.2% this quarter</span>
              </div>
            </div>
            <div className="p-3 bg-status-online/20 rounded-lg">
              <Target className="w-6 h-6 text-status-online" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Standards</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceOverview.compliant}/{complianceOverview.total}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {complianceOverview.partial} partial, {complianceOverview.nonCompliant} non-compliant
              </p>
            </div>
            <div className="p-3 bg-primary-600/20 rounded-lg">
              <Award className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Requirements</p>
              <p className="text-2xl font-bold text-console-text">
                {complianceOverview.requirementsMet}/{complianceOverview.totalRequirements}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {((complianceOverview.requirementsMet / complianceOverview.totalRequirements) * 100).toFixed(0)}% met
              </p>
            </div>
            <div className="p-3 bg-secondary-600/20 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Next Assessment</p>
              <p className="text-2xl font-bold text-console-text">
                {upcomingAssessments.length > 0 
                  ? Math.ceil((upcomingAssessments[0].nextAssessment.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                  : '0'
                }
              </p>
              <p className="text-xs text-console-muted mt-1">
                {upcomingAssessments.length > 0 ? 'days remaining' : 'No upcoming assessments'}
              </p>
            </div>
            <div className="p-3 bg-warning-600/20 rounded-lg">
              <Calendar className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Upcoming Assessments */}
      {upcomingAssessments.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-console-text">Upcoming Assessments</h3>
            <Badge variant="severity" severity="warning">
              {upcomingAssessments.length} due soon
            </Badge>
          </div>
          
          <div className="space-y-3">
            {upcomingAssessments.map((standard) => {
              const daysRemaining = Math.ceil((standard.nextAssessment.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              
              return (
                <div
                  key={standard.id}
                  className={cn(
                    'p-3 border rounded-lg',
                    daysRemaining <= 7 ? 'border-danger-600 bg-danger-600/10' :
                    daysRemaining <= 14 ? 'border-warning-600 bg-warning-600/10' :
                    'border-console-border bg-console-surface/30',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-warning-600" />
                      <div>
                        <div className="font-medium text-console-text">{standard.name} Assessment</div>
                        <div className="text-sm text-console-muted">
                          Due: {formatAbsoluteTime(standard.nextAssessment, 'MMM dd, yyyy')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={cn(
                          'text-sm font-medium',
                          daysRemaining <= 7 ? 'text-danger-600' :
                          daysRemaining <= 14 ? 'text-warning-600' :
                          'text-console-text',
                        )}>
                          {daysRemaining} days
                        </div>
                        <div className="text-xs text-console-muted">remaining</div>
                      </div>
                      
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => handleRunAssessment(standard.id)}
                      >
                        Run Assessment
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
              <input
                type="text"
                placeholder="Search standards and requirements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-console-surface border border-console-border rounded-lg text-console-text placeholder-console-muted focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>
        
        {/* Status Filter Buttons */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-console-muted">
            <Filter className="w-4 h-4" />
            <span>Filter by status:</span>
          </div>
          <div className="flex items-center gap-2">
            {[
              { value: 'compliant', label: 'Compliant', color: 'text-status-online' },
              { value: 'partial', label: 'Partial', color: 'text-warning-600' },
              { value: 'non_compliant', label: 'Non-Compliant', color: 'text-danger-600' },
              { value: 'unknown', label: 'Unknown', color: 'text-console-muted' },
            ].map((status) => {
              const isSelected = statusFilter.includes(status.value);
              return (
                <button
                  key={status.value}
                  onClick={() => {
                    if (isSelected) {
                      setStatusFilter(statusFilter.filter(s => s !== status.value));
                    } else {
                      setStatusFilter([...statusFilter, status.value]);
                    }
                  }}
                  className={cn(
                    'px-3 py-1 rounded-md text-sm font-medium transition-all',
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300',
                  )}
                >
                  {status.label}
                </button>
              );
            })}
            {statusFilter.length > 0 && (
              <button
                onClick={() => setStatusFilter([])}
                className="px-3 py-1 rounded-md text-sm font-medium bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-all"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Standards */}
      <div className="space-y-6">
        {filteredStandards.map((standard) => (
          <Card
            key={standard.id}
            className={cn(
              'p-0 overflow-hidden cursor-pointer transition-colors',
              selectedStandard === standard.id 
                ? 'border-primary-600 bg-primary-600/5' 
                : 'hover:bg-console-surface/30',
            )}
          >
            <div
              className="p-6"
              onClick={() => setSelectedStandard(selectedStandard === standard.id ? null : standard.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    'p-3 rounded-lg',
                    standard.status === 'compliant' ? 'bg-status-online/20' :
                    standard.status === 'partial' ? 'bg-warning-600/20' :
                    standard.status === 'non_compliant' ? 'bg-danger-600/20' :
                    'bg-console-surface/50',
                  )}>
                    <Shield className={cn(
                      'w-6 h-6',
                      standard.status === 'compliant' ? 'text-status-online' :
                      standard.status === 'partial' ? 'text-warning-600' :
                      standard.status === 'non_compliant' ? 'text-danger-600' :
                      'text-console-muted',
                    )} />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-console-text">{standard.name}</h3>
                      <Badge variant="severity" severity={getStatusSeverity(standard.status)}>
                        {standard.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-console-muted">
                      <span>Score: {standard.score.toFixed(1)}%</span>
                      <span>Requirements: {standard.requirements.length}</span>
                      <span>Last assessed: {formatRelativeTime(standard.lastAssessment)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-console-text">
                      {standard.score.toFixed(0)}%
                    </div>
                    <div className="text-xs text-console-muted">Compliance</div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button variant="secondary" size="sm">
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleRunAssessment(standard.id);
                      }}
                    >
                      <Settings className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleExportReport(standard.id);
                      }}
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {selectedStandard === standard.id && (
              <div className="border-t border-console-border p-6 bg-console-surface/20">
                <h4 className="font-medium text-console-text mb-4">Requirements</h4>
                
                <div className="space-y-3">
                  {standard.requirements.map((requirement: any) => (
                    <div
                      key={requirement.id}
                      className={cn(
                        'p-3 border rounded cursor-pointer transition-colors',
                        selectedRequirement === requirement.id
                          ? 'border-primary-600 bg-primary-600/10'
                          : requirement.status === 'met' 
                          ? 'border-status-online/50 bg-status-online/10'
                          : requirement.status === 'partial'
                          ? 'border-warning-600/50 bg-warning-600/10'
                          : requirement.status === 'not_met'
                          ? 'border-danger-600/50 bg-danger-600/10'
                          : 'border-console-border bg-console-surface/30',
                      )}
                      onClick={() => setSelectedRequirement(selectedRequirement === requirement.id ? null : requirement.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-console-text">{requirement.name}</span>
                              <Badge variant="severity" severity={getRequirementStatusSeverity(requirement.status)}>
                                {requirement.status.replace('_', ' ')}
                              </Badge>
                              {requirement.automaticCheck && (
                                <Badge variant="default">Auto</Badge>
                              )}
                            </div>
                            <code className="text-xs text-console-muted">{requirement.id}</code>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-console-muted">
                            {requirement.evidence.length} evidence
                          </span>
                          <span className="text-xs text-console-muted">
                            Checked: {formatRelativeTime(requirement.lastChecked)}
                          </span>
                        </div>
                      </div>

                      {selectedRequirement === requirement.id && requirement.evidence.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-console-border">
                          <h5 className="font-medium text-console-text mb-2" />
                          <div className="space-y-2">
                            {requirement.evidence.map((evidence: any) => (
                              <div
                                key={evidence.id}
                                className="p-2 bg-console-surface/50 rounded border border-console-border"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-primary-600" />
                                    <span className="text-sm text-console-text">{evidence.source}</span>
                                    <Badge variant="default">{evidence.type}</Badge>
                                  </div>
                                  
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-console-muted">
                                      {formatRelativeTime(evidence.timestamp)}
                                    </span>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        handleViewEvidence(evidence.id);
                                      }}
                                    >
                                      <Eye className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="mt-1 text-xs text-console-muted">
                                  Hash: <code className="bg-console-surface/50 px-1 rounded">
                                    {evidence.hash.substring(0, 20)}...
                                  </code>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {filteredStandards.length === 0 && (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-console-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-console-text mb-2">No Compliance Standards Found</h3>
          <p className="text-console-muted mb-6">
            {searchTerm || statusFilter.length > 0 
              ? 'Try adjusting your search or filters' 
              : 'Get started by adding a compliance standard'
            }
          </p>
          {!searchTerm && statusFilter.length === 0 && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Standard
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Helper functions
function getStatusSeverity(status: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (status) {
    case 'compliant': return 'info';
    case 'partial': return 'warning';
    case 'non_compliant': return 'error';
    case 'unknown': return 'warning';
    default: return 'info';
  }
}

function getRequirementStatusSeverity(status: string): 'info' | 'warning' | 'error' | 'critical' {
  switch (status) {
    case 'met': return 'info';
    case 'partial': return 'warning';
    case 'not_met': return 'error';
    case 'not_applicable': return 'info';
    default: return 'info';
  }
}